import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { buildReportLabels } from '../../../utils/reportLabels';

const useAnnouncementApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const params = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = params;
    setFetching(true);
    api.get(`/v1/announcements?${new URLSearchParams(params)}`)
      .then((res) => {
        setData(res.data?.data?.data || []);
        setPagination({
          totalRowCount: res.data?.data?.totalRowCount || 0,
          pageSize: res.data?.data?.pageSize || 10,
          pageIndex: (res.data?.data?.pageIndex || 1) - 1,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const reload = useCallback(() => setRefreshTrigger((v) => v + 1), []);

  const create = useCallback(async (payload) => {
    try {
      await api.post('/v1/announcements', payload);
      reload();
      return true;
    } catch { return false; }
  }, [reload]);

  const update = useCallback(async (id, payload) => {
    try {
      await api.put(`/v1/announcements/${id}`, payload);
      reload();
      return true;
    } catch { return false; }
  }, [reload]);

  const _delete = useCallback(async (ids) => {
    try {
      await Promise.all(ids.map((id) => api.delete(`/v1/announcements/${id}`)));
      reload();
      return true;
    } catch { return false; }
  }, [reload]);

  const objectSchema = useMemo(() => [
    { name: 'title', label: t('announcement.title'), type: 'text', grid: 12, required: true },
    { name: 'description', label: t('announcement.description'), type: 'textarea', grid: 12, required: true },
    {
      name: 'imageUrl',
      label: t('announcement.imageUrl'),
      type: 'pictureUpload',
      grid: 12,
      accept: 'image/*',
      validTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      maxSize: 5,
      purpose: 'announcementImage',
    },
    { name: 'detailUrl', label: t('announcement.detailUrl'), type: 'text', grid: 12, description: t('announcement.detailUrlHint') },
    { name: 'expiryDate', label: t('announcement.expiryDate'), type: 'date', grid: 12 },
  ], [t]);

  const columns = useMemo(() => ([
    { name: 'title', label: t('announcement.title'), options: { filter: false, sort: true } },
    {
      name: 'status',
      label: t('announcement.status'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => t(`announcement.statusValues.${v}`),
      },
    },
    {
      name: 'expiryDate',
      label: t('announcement.expiryDate'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => (v ? new Date(v).toLocaleDateString() : '-'),
      },
    },
    {
      name: 'publishedAt',
      label: t('announcement.publishedAt'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => (v ? new Date(v).toLocaleString() : '-'),
      },
    },
  ]), [t]);

  return { fetching, data, pagination, columns, reload, objectSchema, create, update, _delete };
};

export { useAnnouncementApi };

// Direct API
export const getAnnouncement = (id) => api.get(`/v1/announcements/${id}`);
export const createAnnouncement = (payload) => api.post('/v1/announcements', payload);
export const updateAnnouncement = (id, payload) => api.put(`/v1/announcements/${id}`, payload);
export const deleteAnnouncement = (id) => api.delete(`/v1/announcements/${id}`);
export const publishAnnouncement = (id, payload) => api.post(`/v1/announcements/${id}/publish`, payload);
export const getMyAnnouncements = () => api.get('/v1/announcements/my');

// ── Yayın gövdesi (AKM POC §8): ünvan + personel statüsü hedefleri ve hariçleri, iki kanal. ──
// Seçicinin değerinde olmayan liste [] gider (seçici statü listesini yalnız açıkken taşır); `autoApplyOnEnter`
// duyuruda bir kavram değil — GİTMEZ.
const TARGET_LISTS = ['userIds', 'departmentIds', 'locationIds', 'userGroupIds', 'subTenantIds', 'titleIds', 'employeeStatusIds'];

const pickLists = (source) => Object.fromEntries(
  TARGET_LISTS.map((key) => [key, Array.isArray(source?.[key]) ? source[key] : []]),
);

export const buildPublishPayload = (targets, { sendNotification, sendEmail } = {}) => ({
  ...pickLists(targets),
  includeAll: !!targets?.includeAll,
  excludes: pickLists(targets?.excludes),
  sendNotification: !!sendNotification,
  sendEmail: !!sendEmail,
});

// Açılma kaydı — detay yüklenince bir kez; sessiz (toast yok, hata yutulur: kayıt düşse de ekran düşmez).
export const markAnnouncementOpened = (id) =>
  api.post(`/v1/announcements/${id}/open`, { channel: 'web' }, { skipErrorToast: true }).catch(() => null);

// Alan yoksa (eski sunucu) işaret YOK — yalnız açıkça false olan "açılmamış".
export const isUnopened = (item) => item?.isOpened === false;

// ── Okunma sekmesi (AKM POC §8, AN.Write) — hedef kitle o anki kurallarla canlı; Excel/PDF Raporlar altyapısından. ──

export const getAnnouncementReadSummary = (id) => api.get(`/v1/announcements/${id}/reads/summary`);

export const readsExportUrl = (id) => `/v1/announcements/${id}/reads/export`;

// Kişi listesi (sunucu sayfalı). GET reddetmez: yüklenemedi = `status === false` → unavailable.
export const useAnnouncementReadersApi = (id, queryParams) => {
  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (!id) return undefined;
    let alive = true;
    setFetching(true);
    api.get(`/v1/announcements/${id}/reads`, { params: queryParams || { pageNumber: 1, pageSize: 10 } })
      .then((res) => {
        if (!alive) return;
        if (res?.data?.status === false) {
          setUnavailable(true);
          setData([]);
          setPagination((p) => ({ ...p, totalRowCount: 0 }));
          return;
        }
        const result = res?.data?.data;
        setUnavailable(false);
        setData(result?.data || []);
        setPagination({
          totalRowCount: result?.totalRowCount || 0,
          pageSize: result?.pageSize || 10,
          pageIndex: (result?.pageIndex || 1) - 1,
        });
      })
      .finally(() => { if (alive) setFetching(false); });
    return () => { alive = false; };
  }, [id, queryParams]);

  return { fetching, data, pagination, unavailable };
};

// Sunucu kaynağının (AnnouncementReadReportSource.TurkishDefaults) 26 etiket anahtarı → announcement.reads.<anahtar>.
export const READS_LABEL_KEYS = Object.fromEntries([
  'reportTitle', 'publishedAt', 'filters', 'status', 'department', 'all', 'opened', 'notOpened', 'audience',
  'openedCount', 'notOpenedCount', 'openRate', 'web', 'mobile', 'departmentBreakdown', 'fullName', 'registrationNumber',
  'jobTitle', 'firstOpenedAt', 'channel', 'noDepartment', 'preparedBy', 'generatedAt', 'truncatedNote', 'peopleSheet',
  'departmentsSheet',
].map((key) => [key, `announcement.reads.${key}`]));

// Raporlar ortak etiketleri + başlık + 26 duyuru etiketi (dosya kullanıcının dilinde).
export const buildReadsExportLabels = (t) => buildReportLabels(t, [], t('announcement.reads.reportTitle'),
  Object.fromEntries(Object.entries(READS_LABEL_KEYS).map(([k, key]) => [k, t(key)])));

// Dışa aktarma sorgusu: yalnız dolu süzgeç + sıralama (sayfa YOK — dosya tam veridir).
const READS_EXPORT_PARAMS = ['status[eq]', 'departmentId[eq]', 'fullName[like]', 'orderBy', 'orderDir'];

export const buildReadsExportParams = (queryParams) => Object.fromEntries(
  READS_EXPORT_PARAMS
    .filter((key) => queryParams?.[key] !== undefined && queryParams?.[key] !== null && queryParams?.[key] !== '')
    .map((key) => [key, queryParams[key]]),
);

export const READ_STATUSES = ['opened', 'notOpened'];
export const NO_DEPARTMENT = 'none';

const departmentOptionLabel = (t, departments, value) => {
  if (value === NO_DEPARTMENT) return t('announcement.reads.noDepartment');
  return departments.find((d) => d.departmentId === value)?.departmentName || value;
};

const channelLabel = (t, channel) => {
  if (channel === 'web') return t('announcement.reads.web');
  if (channel === 'mobile') return t('announcement.reads.mobile');
  return '-';
};

/**
 * Okunma kişi listesi kolonları. Kolon `name` = sunucu parametre adı (parseTableParams süzgeç/sıralama anahtarı):
 * fullName[like], departmentId[eq] ('none' = departmansız), status[eq] (opened | notOpened); sortKey sunucunun orderBy'ı.
 * `rows` o anki sayfanın satırlarıdır (departman adı ve açtı/açmadı rozeti satırdan okunur).
 */
export const buildReadersColumns = ({ t, departments = [], rows = [], locale = 'tr', renderStatus }) => {
  const deptValues = [...new Set([
    ...departments.filter((d) => d.departmentId).map((d) => d.departmentId),
    NO_DEPARTMENT,
  ])];
  const statusText = (opened) => t(opened ? 'announcement.reads.opened' : 'announcement.reads.notOpened');
  return [
    {
      name: 'fullName',
      label: t('announcement.reads.fullName'),
      options: {
        filter: true, sort: true, sortKey: 'fullname', filterType: 'textField', filterOptions: { defaultOp: 'like' },
      },
    },
    {
      name: 'registrationNumber',
      label: t('announcement.reads.registrationNumber'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'departmentId',
      label: t('announcement.reads.department'),
      options: {
        filter: true, sort: true, sortKey: 'departmentname', filterType: 'dropdown',
        filterOptions: {
          names: deptValues,
          defaultOp: 'eq',
          renderValue: (v) => departmentOptionLabel(t, departments, v),
        },
        customFilterListOptions: {
          render: (v) => `${t('announcement.reads.department')}: ${departmentOptionLabel(t, departments, v)}`,
        },
        customBodyRender: (v, meta) => rows[meta?.rowIndex]?.departmentName || t('announcement.reads.noDepartment'),
      },
    },
    {
      name: 'titleName',
      label: t('announcement.reads.jobTitle'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'status',
      label: t('announcement.reads.status'),
      options: {
        filter: true, sort: false, filterType: 'dropdown',
        filterOptions: {
          names: READ_STATUSES,
          defaultOp: 'eq',
          renderValue: (v) => statusText(v === 'opened'),
        },
        customFilterListOptions: { render: (v) => `${t('announcement.reads.status')}: ${statusText(v === 'opened')}` },
        customBodyRender: (v, meta) => {
          const opened = !!rows[meta?.rowIndex]?.opened;
          return renderStatus ? renderStatus(opened, statusText(opened)) : statusText(opened);
        },
      },
    },
    {
      name: 'firstOpenedAt',
      label: t('announcement.reads.firstOpenedAt'),
      options: {
        filter: false, sort: true, sortKey: 'firstopenedat',
        customBodyRender: (v) => (v ? new Date(v).toLocaleString(locale) : '-'),
      },
    },
    {
      name: 'channel',
      label: t('announcement.reads.channel'),
      options: { filter: false, sort: false, customBodyRender: (v) => channelLabel(t, v) },
    },
  ];
};

// Açılma oranı (0..100) dile göre yüzde: tr "%66,7", en "66.7%".
export const formatOpenRate = (rate, locale = 'tr') => {
  const n = Number(rate) || 0;
  try {
    return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }).format(n / 100);
  } catch {
    return `${n}%`;
  }
};

